/* global DOMParser */

/** @typedef {{ id: string, label: string, searchText?: string }} MentionUser */

/**
 * Map assignee dropdown options to mention picker users.
 * @param {Array<{ value?: string, label?: string }>} options
 * @returns {MentionUser[]}
 */
export function mapOptionsToMentionUsers(options = []) {
  const seen = new Set();
  const users = [];
  for (const opt of options) {
    const id = opt?.value != null ? String(opt.value).trim() : "";
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const label = String(opt?.label ?? "").trim() || id;
    users.push({
      id,
      label,
      searchText: label.toLowerCase(),
    });
  }
  return users;
}

/**
 * @param {MentionUser[]} users
 * @param {string} query — empty shows full list (e.g. right after typing @)
 */
export function filterMentionUsers(users, query) {
  const q = String(query ?? "")
    .trim()
    .toLowerCase();
  if (!q) return users;
  return users.filter((u) => u.searchText.includes(q));
}

/**
 * Detect active @mention query from text before the caret.
 * @param {string} textBeforeCaret
 * @returns {{ query: string, atIndex: number } | null}
 */
export function getActiveMentionQuery(textBeforeCaret) {
  const text = String(textBeforeCaret ?? "");
  const match = text.match(/(^|[\s\u00a0])@([\w\s.'-]*)$/);
  if (!match) return null;
  const query = match[2] ?? "";
  const atIndex = text.length - query.length - 1;
  return { query, atIndex };
}

/**
 * Collect unique user ids from mention spans in comment HTML.
 * @param {string} html
 * @returns {string[]}
 */
export function extractMentionedUserIdsFromHtml(html) {
  if (!html || typeof html !== "string") return [];
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const nodes = doc.querySelectorAll("[data-user-id], .comment-mention[data-user-id]");
    const ids = [];
    nodes.forEach((node) => {
      const id = node.getAttribute("data-user-id");
      if (id && String(id).trim()) ids.push(String(id).trim());
    });
    return [...new Set(ids)];
  } catch {
    return [];
  }
}

/**
 * @param {string} userId
 * @param {string} label
 */
export function buildMentionSpanElement(userId, label) {
  const span = document.createElement("span");
  span.className = "comment-mention";
  span.setAttribute("data-user-id", String(userId));
  span.setAttribute("contenteditable", "false");
  span.textContent = `@${label}`;
  return span;
}

/**
 * Plain-text fallback token: @[userId:Display Name]
 * @param {string} content
 */
export function linkifyPlainTextMentions(content) {
  if (!content || typeof content !== "string") return content;
  if (content.includes("comment-mention") || content.includes("<")) return content;
  const escaped = content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped.replace(
    /@\[([^:\]]+):([^\]]+)\]/g,
    '<span class="comment-mention" data-user-id="$1">@$2</span>'
  );
}

/** Remove trailing spaces, nbsp entities, and non-breaking spaces from comment text/HTML. */
export function stripTrailingCommentWhitespace(content) {
  if (content == null || content === "") return "";
  let s = String(content);
  s = s.replace(/(?:&nbsp;|\u00a0|\s)+(<\/[^>]+>\s*)+$/gi, "$1");
  return s.replace(/(?:&nbsp;|\u00a0|\s)+$/gi, "");
}

/** Decode HTML entities (including double-encoded API payloads). */
export function decodeHtmlEntities(text) {
  if (text == null || text === "") return "";
  let s = String(text);
  const entityPattern = /&(?:lt|gt|amp|quot|#039|apos|nbsp|#\d+|#x[\da-f]+);/i;
  for (let pass = 0; pass < 2; pass += 1) {
    if (!entityPattern.test(s)) break;
    const textarea = document.createElement("textarea");
    textarea.innerHTML = s;
    const decoded = textarea.value;
    if (decoded === s) break;
    s = decoded;
  }
  return s;
}

/**
 * @param {string} html
 */
export function normalizeCommentHtmlForDisplay(html) {
  if (!html) return "";
  const cleaned = stripTrailingCommentWhitespace(decodeHtmlEntities(html)).trim();
  if (!cleaned) return "";
  if (cleaned.includes("<")) return cleaned;
  return linkifyPlainTextMentions(cleaned);
}
